
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

declare global {
  interface WindowEventMap {
    'beforeinstallprompt': BeforeInstallPromptEvent;
    'appinstalled': Event;
  }
}

const PwaInstallPrompt: React.FC = () => {
  const { t } = useTranslationUtils();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Capture the install prompt
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowPrompt(true);
    };

    // Handle app installed
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setIsInstalled(true);
      console.log('App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      console.log(`User ${outcome} the install prompt`);
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error during PWA installation:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Don't show prompt again for this session
    localStorage.setItem('pwaPromptDismissed', 'true');
  };

  if (isInstalled || !showPrompt || localStorage.getItem('pwaPromptDismissed') === 'true') {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-80">
      <Card className="border-primary/20 shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">{t('pwa.installTitle', 'Install App')}</CardTitle>
          <CardDescription>
            {t('pwa.installDescription', 'Get the best experience by installing our app')}
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-between pt-2">
          <Button variant="ghost" onClick={handleDismiss}>
            {t('pwa.notNow', 'Not Now')}
          </Button>
          <Button onClick={handleInstallClick} className="gap-2">
            <Download className="h-4 w-4" />
            {t('pwa.install', 'Install')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PwaInstallPrompt;
